/**
 * Audit events for a merged document, recorded against the PROJECT TDO
 * (VE-31977).
 *
 * WHY THIS IS NOT IN `documents/sagas/auditLogs.ts`, where three of these
 * events started: that file emits through `LOG_AUDIT_EVENT`, whose saga falls
 * back to the open session's tdoId when the action names none — and a project
 * is NEVER the open session. `routeSaga` resolves a project URL to its host
 * member and says so outright: "the session belongs to the member, and a
 * project TDO's own answer says nothing about the file on screen." So every
 * set-wide act was being filed against `project.members[0]`: that file's own
 * per-file CSV claimed the act as its own, the merged log lost the whole
 * stamping history the moment member[0] was removed or reordered out of first
 * place, and the SOURCE FILE column named a file that had nothing to do with
 * it.
 *
 * Everything here therefore names the project TDO explicitly, through
 * `emitTdoAuditEvent` — the emitter built for surfaces acting on a file they
 * have not opened. Several of these acts fire from the FOLDER VIEW with no
 * editor session at all (create, delete, Manage Files), where the document
 * editor's emitter would have written nothing whatsoever.
 *
 * SUCCESS ONLY, never a request. The `CLEAR_PROJECT_BATES_SUCCESS` action
 * exists because the first cut hung its event off the request and recorded
 * "Removed Bates stamping" over a release whose numbering was never removed.
 *
 * NO MEMBERSHIP EVENT NAMES A FILE. See `describeMemberIds` — the merged CSV
 * ships inside the release, and a document pulled OUT of a release must not
 * have its filename published by the release.
 *
 * FIRE AND FORGET, and guarded. An audit write is best-effort everywhere in
 * this app, and these ones sit on the critical path of a membership write, a
 * project creation and a release — none of which may be delayed, failed or
 * restarted by the audit endpoint. See `emitProjectAuditEvent`.
 */
import { put, takeEvery } from 'typed-redux-saga/macro';

import emitTdoAuditEvent from '@helpers/emitTdoAuditEvent';
import type { TDOId } from '@common-modules/universal/models/Brands';

import { describeBatesStamp } from '../documents/batesLabel';
import {
  CLEAR_PROJECT_BATES_SUCCESS,
  DELETE_COMBINED_SUCCESS,
  DELETE_PROJECT_SUCCESS,
  INCREMENT_COMBINED_DOWNLOAD,
  SET_PROJECT_BATES_SUCCESS,
  SET_PROJECT_WATERMARKS_SUCCESS,
} from './actions';

/** Dispatch breadcrumbs, this module's own so nothing downstream collides. */
const PROJECT_AUDIT_ACTION_TYPES: readonly [string, string, string] = [
  'redact/projects/EMIT_AUDIT_INIT',
  'redact/projects/EMIT_AUDIT_SUCCESS',
  'redact/projects/EMIT_AUDIT_FAILURE',
];

/**
 * The SOURCE FILE a project's own rows carry in the merged CSV.
 *
 * Not blank: a blank cell beside a filled SOURCE TDO reads as a name nobody
 * supplied rather than as "this is the set itself".
 */
export const MERGED_DOCUMENT_SOURCE_NAME = '(merged document)';

/**
 * One event against the project TDO. BEST-EFFORT, and it never blocks.
 *
 * NOT AWAITED, because there is nothing left to wait for. The first attempt
 * awaited the mutation to guarantee that a release's own event reached the
 * platform before the merged CSV was gathered from it; the CSV is now handed
 * that row directly (see `combineSaga` and `buildMergedAuditLogCsv`'s
 * `extraRecords`), so the wait bought nothing.
 *
 * An earlier version of this comment justified it differently — that
 * `fetchGraphQLApi` carries no `AbortController`, so an awaited emit against
 * an endpoint that never answers could never be bounded. That is wrong inside
 * a saga: `race({ done: call(...), timeout: delay(n) })` bounds an unabortable
 * promise without wedging the caller, and `sagas.ts` uses exactly that pattern.
 * The unbounded await WAS dangerous — `handleUpdateProject` runs inside a
 * `while(true)` over a sliding channel, so one hung emit would have dropped
 * every membership write for the rest of the session — but "it could not have
 * been bounded" was not the reason, and a reason that does not hold is worse
 * than none.
 *
 * GUARDED, because `emitTdoAuditEvent` catches its own REJECTION and not a
 * synchronous throw out of the thunk body: `configModule.getConfig(state)`
 * destructures an unguarded return, and redux-saga propagates a sync throw out
 * of `put`. Unguarded, that threw into `handleCombineProject`'s outer catch
 * and reported COMBINE_PROJECT_FAILURE — "Could not create the merged
 * document" — over a release that was already built, saved and downloadable,
 * with the previous version's assets already rotated away. In the create and
 * membership handlers it escaped to `keepAlive`, which restarts `projectsSaga`
 * wholesale and discards the membership channel's buffer mid-write.
 *
 * `extra` is merged into the event payload last, so a caller that must know
 * the exact row it emitted can pin `timestamp`.
 */
export function* emitProjectAuditEvent(
  projectTdoId: TDOId,
  action: string,
  details?: string,
  extra?: Record<string, unknown>
) {
  try {
    yield* put(
      emitTdoAuditEvent(
        String(projectTdoId),
        action,
        { ...(details ? { details } : {}), ...extra },
        PROJECT_AUDIT_ACTION_TYPES
      ) as never
    );
  } catch (error) {
    // Fixed string plus the correlation keys; the rejection path logs its own.
    console.warn('[redact] merged-document audit event not emitted', {
      projectTdoId,
      action,
      error,
    });
  }
}

// ---------------------------------------------------------------------------
// Describing a membership
// ---------------------------------------------------------------------------

/**
 * How many entries a list spells out before it gives a count.
 *
 * The same bound the bulk-detection events use. A fifty-file request would
 * otherwise put fifty entries in one CSV cell, and the question the trail has
 * to answer is already answered by the first few plus a count.
 */
const MAX_LISTED = 10;

/**
 * How much of an operator-authored string an entry carries. The item COUNT was
 * bounded from the start and the item LENGTH was not, so ten unbounded strings
 * could still fill a cell.
 */
const MAX_TEXT_LENGTH = 120;

export const clip = (text: string): string =>
  text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}…` : text;

const withCount = (parts: readonly string[], total: number): string => {
  const rest = total - parts.length;
  return rest > 0 ? `${parts.join(', ')} and ${rest} more` : parts.join(', ');
};

/**
 * DOCUMENTS ARE IDENTIFIED BY ID. There is no function here that renders a
 * filename, and that is the point — this is a disclosure rule, not a style,
 * and a vocabulary that cannot express a name cannot leak one.
 *
 * The merged CSV is not an internal document. `downloadMergedWithAuditLogs`
 * hands it to the reviewer in the same download as the merged PDF, so it
 * travels with the release by default. An event that named files would
 * therefore publish, inside a release, the filename of documents that are not
 * in it — which is exactly the set counsel decided to withhold. In this domain
 * filenames carry subject names, case numbers and charge codes; the product
 * exists to stop precisely that leaving the building.
 *
 * TWO ROUNDS OF THIS WERE NEEDED, so the reasoning is worth keeping. The first
 * attempt named every member. The second kept names out of MEMBERSHIP events
 * but let the build event name the documents it shipped, on the grounds that
 * those are in the release by definition — true of THAT release, and false of
 * every one after it, because `fetchEventsForSource` pages a project's whole
 * history with no filter and so every later build republishes the earlier
 * build's row. A document named by any event is named in every release that
 * follows. The only rule that holds is the one with no exceptions.
 *
 * What is lost is readability for the agency's own reading, and it is bounded:
 * for the release being shipped, every member is its own gather source, so its
 * filename is already in the CSV's SOURCE FILE column. Every other id resolves
 * through the platform for anyone with access to the request.
 */
export function describeMemberIds(members: readonly unknown[]): string {
  if (!members.length) {
    return 'none';
  }
  const ids = withCount(
    members.slice(0, MAX_LISTED).map((tdoId) => String(tdoId)),
    members.length
  );
  return `${members.length} document(s) (${ids})`;
}

/**
 * What one membership write DID, or null when it did nothing.
 *
 * ORDER IS MEMBERSHIP, so a write that only moves two files is a real change
 * and gets its own event: `updateProjectMembers` says it outright — "the order
 * IS the membership: it sets page ranges and the Bates run" — and a drag that
 * reorders two files renumbers the whole production.
 *
 * The resulting list rides on every change, not just the reorder: a trail that
 * says a document was added without saying where it landed cannot reconstruct
 * the numbering that release went out under.
 */
export function describeMembershipChange(
  // BARE IDS, so the disclosure rule is unrepresentable rather than merely
  // observed. An earlier cut took `{ tdoId, name }` and read only the id,
  // which left a `name` sitting in scope at every call site for the next
  // person to reach for — and a saga resolving filenames nothing rendered.
  previous: readonly (string | TDOId)[],
  next: readonly (string | TDOId)[]
): { readonly action: string; readonly details: string } | null {
  const previousIds = previous.map(String);
  const nextIds = next.map(String);
  const added = nextIds.filter((id) => !previousIds.includes(id));
  const removed = previousIds.filter((id) => !nextIds.includes(id));

  if (!added.length && !removed.length) {
    const reordered =
      previousIds.length !== nextIds.length ||
      previousIds.some((id, at) => id !== nextIds[at]);
    // The panel writes on every tick and the folder view rewrites the whole
    // list, so a write that changed nothing is routine — and must not record
    // a change.
    return reordered
      ? {
          action: 'Reordered merged document',
          details: `now ${describeMemberIds(nextIds)}`,
        }
      : null;
  }

  const parts: string[] = [];
  if (added.length) {
    parts.push(`Added ${describeMemberIds(added)}`);
  }
  if (removed.length) {
    parts.push(`Removed ${describeMemberIds(removed)}`);
  }
  parts.push(`now ${describeMemberIds(nextIds)}`);
  return {
    action:
      added.length && removed.length
        ? 'Changed merged document contents'
        : added.length
          ? 'Added documents to merged document'
          : 'Removed documents from merged document',
    details: parts.join('; '),
  };
}

// ---------------------------------------------------------------------------
// The acts whose data is already on the action
// ---------------------------------------------------------------------------

/**
 * A merged document's stamping was applied or changed (VE-30105).
 *
 * The version is recorded with the settings because that is what a burned
 * output carries (`batesProjectVersion`): without it the trail says a stamp
 * was applied but not which apply produced the numbers on a given release.
 */
export function* onSetProjectBatesStamp(
  action: ReturnType<typeof SET_PROJECT_BATES_SUCCESS>
) {
  const { projectTdoId, batesStamp } = action.payload;
  yield* emitProjectAuditEvent(
    projectTdoId,
    'Applied Bates stamping (merged document)',
    `${describeBatesStamp(batesStamp.settings)} (v${batesStamp.modifiedDateTime})`
  );
}

/** A merged document's stamping was switched off. */
export function* onClearProjectBatesStamp(
  action: ReturnType<typeof CLEAR_PROJECT_BATES_SUCCESS>
) {
  yield* emitProjectAuditEvent(
    action.payload.projectTdoId,
    'Removed Bates stamping (merged document)'
  );
}

/**
 * The identifier pair a watermark act is cited by.
 *
 * BOTH, because they answer different questions — and ONLY THE ASSET ID
 * ACTUALLY JOINS.
 *
 * The asset id is the server's, it is what a released output's provenance
 * records, and it does not collide. It is also new here: the original
 * destructured `watermarksModifiedDateTime` twice — once as `version` — and
 * then fell back `watermarksModifiedDateTime ?? assetId`, which on every
 * success path takes the timestamp, so the entry rendered `v<ts> · <ts>` and
 * the asset id was never written at all. `assetId` is legitimately null
 * between an optimistic write and its confirmation, so the guard earns its
 * place.
 *
 * THE VERSION IS NOT THE ONE THE SERVER STORED, and the trail should not be
 * read as though it were. `projectRedactionSaga` writes the record with a
 * stamp minted at the moment of the write, but dispatches SUCCESS carrying the
 * OPTIMISTIC stamp the request reducer minted — so the server's record and
 * `mergedSetStaleness` hold one timestamp and this entry holds an earlier one.
 * It is recorded anyway because it is what the reviewer's session believed and
 * it bounds the act in time; it is not a key. Fixing it belongs in
 * `projectRedactionSaga`, not here.
 */
function watermarkStamp(version: string | null, assetId: string | null) {
  return `v${version}${assetId ? ` · ${assetId}` : ''}`;
}

/**
 * A merged document's watermarking was applied or changed (VE-30104).
 *
 * THE PER-DOCUMENT APPLY IS STILL NOT AUDITED — a gap this ticket did not
 * widen into, as the original watcher noted.
 */
export function* onSetProjectWatermarks(
  action: ReturnType<typeof SET_PROJECT_WATERMARKS_SUCCESS>
) {
  const { projectTdoId } = action.payload;
  const {
    watermarks,
    watermarksOff,
    watermarksModifiedDateTime: version,
    assetId,
  } = action.payload.data;
  const stamp = watermarkStamp(version, assetId);
  if (watermarks.length === 0) {
    yield* emitProjectAuditEvent(
      projectTdoId,
      'Removed watermarks (merged document)',
      stamp
    );
    return;
  }
  // Text is already bounded to MAX_WATERMARK_TEXT_LENGTH by the sanitizer
  // every mark passes through, and the list is capped here for the same
  // reason the member lists are: a set can carry more marks than a cell.
  const described = withCount(
    watermarks
      .slice(0, MAX_LISTED)
      .map((mark) => (mark.type === 'text' ? `"${clip(mark.text)}"` : 'image')),
    watermarks.length
  );
  yield* emitProjectAuditEvent(
    projectTdoId,
    'Applied watermarks (merged document)',
    `${described}${watermarksOff ? ' (hidden)' : ''} (${stamp})`
  );
}

/**
 * The built merged PDF was withdrawn.
 *
 * The project survives this — only the released artifact goes — so the event
 * is readable afterwards in the usual way.
 */
export function* onDeleteCombined(
  action: ReturnType<typeof DELETE_COMBINED_SUCCESS>
) {
  yield* emitProjectAuditEvent(
    action.payload.projectTdoId,
    'Withdrew merged document output'
  );
}

/**
 * The merged document itself was deleted.
 *
 * RECORDED EVEN THOUGH THE TDO IS ABOUT TO GO. The platform's event store
 * outlives the TDO, so the act is still answerable there; what does not
 * survive is the merged-log ASSET, which went with the project. That is the
 * honest state of affairs and the reason this event is worth emitting rather
 * than skipping: it is the only remaining trace that the set existed.
 */
export function* onDeleteProject(
  action: ReturnType<typeof DELETE_PROJECT_SUCCESS>
) {
  yield* emitProjectAuditEvent(
    action.payload.projectTdoId,
    'Deleted merged document'
  );
}

/**
 * The merged PDF went out.
 *
 * On the action the DOWNLOAD dispatches, not on the count write that follows:
 * the file left the building either way, and a failed counter update must not
 * erase the fact that it did.
 */
export function* onCombinedDownload(
  action: ReturnType<typeof INCREMENT_COMBINED_DOWNLOAD>
) {
  yield* emitProjectAuditEvent(
    action.payload.projectTdoId,
    'Downloaded merged document'
  );
}

/**
 * The watchers whose acts carry everything they need on the action.
 *
 * ITS OWN ROOT ENTRY in `common-app/state/sagas`, not a fork inside
 * `projectsSaga`. `keepAlive` restarts a root saga wholesale, and
 * `projectsSaga` owns the sliding membership channel and the Bates/watermark
 * write queue — so a throw in an audit watcher would have torn down a write
 * queue mid-write. Two of these watchers also belong to `combineProjectSaga`'s
 * domain rather than `projectsSaga`'s; a root of their own is the only
 * placement that does not pick the wrong owner for one of them.
 *
 * The rest — create, membership, rename, build — are emitted from the handlers
 * that run them, because only those hold the thing the event has to say: the
 * membership the write replaced, the name a rename overwrote, and the version
 * the server issued.
 */
export function* projectAuditSaga() {
  yield* takeEvery(SET_PROJECT_BATES_SUCCESS, onSetProjectBatesStamp);
  yield* takeEvery(CLEAR_PROJECT_BATES_SUCCESS, onClearProjectBatesStamp);
  yield* takeEvery(SET_PROJECT_WATERMARKS_SUCCESS, onSetProjectWatermarks);
  yield* takeEvery(DELETE_COMBINED_SUCCESS, onDeleteCombined);
  yield* takeEvery(DELETE_PROJECT_SUCCESS, onDeleteProject);
  yield* takeEvery(INCREMENT_COMBINED_DOWNLOAD, onCombinedDownload);
}

export default projectAuditSaga;
